import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import i18n from '@/i18n';
import { systemMaterialAPI } from '@/utils/api';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import { OFFICIAL_MATERIAL_CATEGORIES } from '@/pages/ipe/workbench/constants/homeGalleryConfig';
import { normalizeGalleryItem, filterItemsByDateRange } from '@/pages/ipe/workbench/utils/groupGalleryByDate';
import { resolveStampGroupsFromTemplates } from '@/pages/ipe/workbench/utils/podTemplateMeta';
import LibraryModalImageCard from './LibraryModalImageCard';
import LibraryModalDateGroupedGrid from './LibraryModalDateGroupedGrid';
import LibraryModalDateFilter from './LibraryModalDateFilter';
import OfficialGalleryPreviewModal from './OfficialGalleryPreviewModal';

import {
  prefetchDisplayUrlsForListItems,
  warmDisplayCacheFromRecords,
} from '@/utils/imageDisplayAccess';

const PAGE_SIZE = 200;

const GALLERY_PICKER_CONFIG = {
  material: {
    category: OFFICIAL_MATERIAL_CATEGORIES.material,
    normalizeKey: 'material',
    titleKey: 'materialPicker.materialTitle',
    titleId: 'wf-material-rec-modal-title',
    emptyTextKey: 'materialPicker.materialEmpty',
    emptyDateTextKey: 'materialPicker.materialEmptyDate',
    confirmEmptyToastKey: 'materialPicker.materialConfirmEmpty',
    previewTab: 'material',
  },
  'ai-inspiration': {
    category: OFFICIAL_MATERIAL_CATEGORIES['ai-inspiration'],
    normalizeKey: 'ai-inspiration',
    titleKey: 'materialPicker.aiTitle',
    titleId: 'wf-ai-inspiration-modal-title',
    emptyTextKey: 'materialPicker.aiEmpty',
    emptyDateTextKey: 'materialPicker.aiEmptyDate',
    confirmEmptyToastKey: 'materialPicker.aiConfirmEmpty',
    previewTab: 'ai-inspiration',
  },
};

function mergeItems(prev, rows) {
  if (!rows.length) return prev;
  const seen = new Set(prev.map((item) => item.id));
  const next = [...prev];
  rows.forEach((item) => {
    if (item?.id && !seen.has(item.id)) {
      seen.add(item.id);
      next.push(item);
    }
  });
  return next;
}

/** 从已发布的官方图库选图（素材推荐 / AI 灵感），供 AI 处理与工作流使用 */
export default function MaterialRecommendationPickerModal({
  isOpen,
  onClose,
  onConfirm,
  showToast,
  selectedImageTemplates = [],
  /** @type {'material'|'ai-inspiration'} */
  galleryKey = 'material',
}) {
  const galleryConfig = GALLERY_PICKER_CONFIG[galleryKey] || GALLERY_PICKER_CONFIG.material;
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [apiPage, setApiPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [positionArray, setPositionArray] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const bodyRef = useRef(null);
  const [gridScrollRoot, setGridScrollRoot] = useState(null);
  const fetchGenRef = useRef(0);

  const stampGroups = useMemo(
    () => resolveStampGroupsFromTemplates(selectedImageTemplates),
    [selectedImageTemplates],
  );
  const stampAllocationEnabled = stampGroups.length > 0;

  const dateFilteredItems = useMemo(
    () => filterItemsByDateRange(items, { startDate: filterStartDate, endDate: filterEndDate }),
    [items, filterStartDate, filterEndDate],
  );

  const selectedCount = stampAllocationEnabled
    ? positionArray.filter((id) => id !== null && id !== undefined).length
    : selectedMap.size;
  const loadedAllSelected = dateFilteredItems.length > 0
    && dateFilteredItems.every((item) => item?.id && (
      stampAllocationEnabled ? positionArray.includes(item.id) : selectedMap.has(item.id)
    ));

  const resolveItemThumb = useCallback((item) => item?.url || item?.thumbnailUrl || '', []);

  const fetchPage = useCallback(async (page, { append = false, gen } = {}) => {
    const res = await systemMaterialAPI.list({
      page,
      pageSize: PAGE_SIZE,
      category: galleryConfig.category,
    });
    if (gen != null && gen !== fetchGenRef.current) return null;

    const rows = (res?.items || [])
      .map((item) => normalizeGalleryItem(item, galleryConfig.normalizeKey))
      .filter(Boolean);
    const total = Number(res?.total) || rows.length;

    if (append) {
      setItems((prev) => mergeItems(prev, rows));
    } else {
      setItems(rows);
    }
    setTotalItems(total);
    setApiPage(page);
    return { rows, total };
  }, [galleryConfig.category, galleryConfig.normalizeKey]);

  const loadInitial = useCallback(async () => {
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    setLoading(true);
    setError('');
    setSelectedMap(new Map());
    setPositionArray([]);
    setFilterStartDate('');
    setFilterEndDate('');
    setPreviewItem(null);
    try {
      let page = 1;
      let accumulated = [];
      while (gen === fetchGenRef.current) {
        const result = await fetchPage(page, { append: page > 1, gen });
        if (!result) return;
        accumulated = page === 1 ? result.rows : mergeItems(accumulated, result.rows);
        setItems(accumulated);
        // 仅对当前已加载页做签名预热，避免打开弹窗瞬间全量 sign + 触视口后爆发 view
        if (result.rows.length > 0) {
          warmDisplayCacheFromRecords(result.rows, resolveItemThumb);
          void prefetchDisplayUrlsForListItems(result.rows, resolveItemThumb);
        }
        if (accumulated.length >= result.total || result.rows.length === 0) break;
        page += 1;
      }
    } catch (e) {
      setItems([]);
      setTotalItems(0);
      setError(e?.message || i18n.t('workbench:materialPicker.loadFailed'));
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [fetchPage, resolveItemThumb]);

  useEffect(() => {
    if (!isOpen || dateFilteredItems.length === 0) return undefined;
    // 日期筛选后对当前可见子集再预热一遍签名（不做额外全库扫签）
    warmDisplayCacheFromRecords(dateFilteredItems.slice(0, PAGE_SIZE), resolveItemThumb);
    void prefetchDisplayUrlsForListItems(dateFilteredItems.slice(0, PAGE_SIZE), resolveItemThumb);
    return undefined;
  }, [dateFilteredItems, isOpen, resolveItemThumb]);

  useEffect(() => {
    if (isOpen) {
      loadInitial();
    }
  }, [isOpen, loadInitial]);

  const toggleItem = useCallback((item) => {
    if (!item?.id) return;
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  }, []);

  const handleClearPosition = useCallback((positionIndex) => {
    const itemId = positionArray[positionIndex];
    if (!itemId) return;
    setPositionArray((prev) => {
      const next = [...prev];
      next[positionIndex] = null;
      return next;
    });
    setSelectedMap((prev) => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  }, [positionArray]);

  const handleToggleWithPosition = useCallback((item) => {
    if (!item?.id) return;
    const itemId = item.id;
    const isSelected = positionArray.includes(itemId);
    if (isSelected) {
      const positionIndex = positionArray.indexOf(itemId);
      if (positionIndex >= 0) {
        setPositionArray((prev) => {
          const next = [...prev];
          next[positionIndex] = null;
          return next;
        });
      }
      setSelectedMap((prev) => {
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      });
      return;
    }
    const emptyIndex = positionArray.findIndex((id) => id === null || id === undefined);
    setPositionArray((prev) => {
      const next = [...prev];
      if (emptyIndex >= 0) next[emptyIndex] = itemId;
      else next.push(itemId);
      return next;
    });
    setSelectedMap((prev) => {
      const next = new Map(prev);
      next.set(itemId, item);
      return next;
    });
  }, [positionArray]);

  const toggleLoadedAll = useCallback(() => {
    if (stampAllocationEnabled) return;
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (loadedAllSelected) {
        dateFilteredItems.forEach((item) => {
          if (item?.id) next.delete(item.id);
        });
      } else {
        dateFilteredItems.forEach((item) => {
          if (item?.id) next.set(item.id, item);
        });
      }
      return next;
    });
  }, [dateFilteredItems, loadedAllSelected, stampAllocationEnabled]);

  const handleToggleById = useCallback((imageId) => {
    const item = items.find((row) => row.id === imageId);
    if (!item) return;
    if (stampAllocationEnabled) {
      handleToggleWithPosition(item);
    } else {
      toggleItem(item);
    }
  }, [handleToggleWithPosition, items, stampAllocationEnabled, toggleItem]);

  const handleToggleDateGroup = useCallback((groupItems) => {
    if (stampAllocationEnabled) return;
    const ids = groupItems.map((item) => item?.id).filter(Boolean);
    if (ids.length === 0) return;

    setSelectedMap((prev) => {
      const next = new Map(prev);
      const allGroupSelected = ids.every((id) => next.has(id));
      if (allGroupSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        groupItems.forEach((item) => {
          if (item?.id) next.set(item.id, item);
        });
      }
      return next;
    });
  }, [stampAllocationEnabled]);

  const handleConfirm = () => {
    let selected;
    if (stampAllocationEnabled) {
      const orderedIds = positionArray.filter((id) => id !== null && id !== undefined);
      selected = orderedIds
        .map((id) => selectedMap.get(id) || items.find((row) => row.id === id))
        .filter(Boolean);
    } else {
      selected = [...selectedMap.values()];
    }
    if (selected.length === 0) {
      showToast?.(i18n.t(`workbench:${galleryConfig.confirmEmptyToastKey}`), 'warning');
      return;
    }
    onConfirm?.(selected);
  };

  const showStampArea = stampAllocationEnabled
    && stampGroups.length > 0
    && positionArray.filter((id) => id !== null && id !== undefined).length > 0;

  if (!isOpen) return null;

  return createPortal(
    <>
    <div className="funding-modal funding-modal--stack-top">
      <div className="funding-modal__backdrop" role="presentation" />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--library"
        role="dialog"
        aria-modal="true"
        aria-labelledby={galleryConfig.titleId}
      >
        <header className="funding-modal__header funding-library-modal__header">
          <div className="funding-library-modal__header-left">
            <label className="funding-library-modal__select-all">
              <input
                type="checkbox"
                checked={loadedAllSelected}
                disabled={loading || dateFilteredItems.length === 0 || stampAllocationEnabled}
                onChange={toggleLoadedAll}
              />
              <span>{i18n.t('workbench:materialPicker.selectAll')}</span>
            </label>
            <p className="funding-library-modal__header-hint">
              {i18n.t('workbench:materialPicker.headerHint', { count: selectedCount })}
            </p>
          </div>
          <h3 id={galleryConfig.titleId} className="funding-library-modal__header-title">
            {i18n.t(`workbench:${galleryConfig.titleKey}`)}
          </h3>
          <div className="funding-library-modal__header-right">
            <LibraryModalDateFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={(start, end) => {
                setFilterStartDate(start || '');
                setFilterEndDate(end || '');
              }}
              disabled={loading}
            />
            {dateFilteredItems.length > 0 ? (
              <span className="funding-library-modal__header-hint">
                {i18n.t('workbench:materialPicker.totalCount', { count: dateFilteredItems.length })}
              </span>
            ) : null}
          </div>
        </header>

        <div className={`funding-library-modal__body ${showStampArea ? 'funding-library-modal__body--with-stamps' : 'funding-library-modal__body--without-stamps'}`}>
          <div
            className="funding-library-modal__grid-container"
            ref={(node) => {
              bodyRef.current = node;
              setGridScrollRoot(node);
            }}
          >
            {loading && items.length === 0 ? (
              <p className="funding-library-modal__empty">{i18n.t('workbench:materialPicker.loading')}</p>
            ) : error && items.length === 0 ? (
              <div className="funding-library-modal__empty funding-library-modal__empty--stack">
                <span>{error}</span>
                <button type="button" className="funding-link-button" onClick={loadInitial}>
                  {i18n.t('workbench:materialPicker.retry')}
                </button>
              </div>
            ) : items.length === 0 ? (
              <p className="funding-library-modal__empty">
                {i18n.t(`workbench:${galleryConfig.emptyTextKey}`)}
              </p>
            ) : dateFilteredItems.length === 0 ? (
              <p className="funding-library-modal__empty">
                {i18n.t(`workbench:${galleryConfig.emptyDateTextKey}`)}
              </p>
            ) : (
              <LibraryModalDateGroupedGrid
                items={dateFilteredItems}
                isItemSelected={(item) => (
                  stampAllocationEnabled ? positionArray.includes(item.id) : selectedMap.has(item.id)
                )}
                onToggleDateGroup={handleToggleDateGroup}
                renderItem={(item) => (
                  <LibraryModalImageCard
                    key={item.id}
                    image={item}
                    scrollRoot={gridScrollRoot}
                    selected={stampAllocationEnabled ? positionArray.includes(item.id) : selectedMap.has(item.id)}
                    onToggle={handleToggleById}
                    onPreview={() => setPreviewItem(item)}
                    loadingLabel={i18n.t('workbench:materialPicker.noPreview')}
                  />
                )}
              />
            )}
          </div>

          {showStampArea && (
            <div className="funding-library-modal__stamp-area">
              <div className="funding-library-modal__stamp-title">
                {i18n.t('workbench:materialPicker.stampArea')}
              </div>
              <div className="funding-library-modal__stamp-grid">
                {(() => {
                  const groupCount = Math.max(1, Math.ceil(
                    positionArray.filter((id) => id !== null && id !== undefined).length / stampGroups.length,
                  ));
                  const groups = [];
                  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
                    const groupSquares = stampGroups.map((stampGroup, stampIndex) => {
                      const positionIndex = groupIndex * stampGroups.length + stampIndex;
                      const assignedItemId = positionIndex < positionArray.length ? positionArray[positionIndex] : null;
                      const assignedItem = assignedItemId
                        ? (selectedMap.get(assignedItemId) || items.find((row) => row.id === assignedItemId))
                        : null;
                      return (
                        <div
                          key={`group-${groupIndex}-stamp-${stampIndex}`}
                          className="funding-library-modal__stamp-item"
                        >
                          <div className="funding-library-modal__stamp-square">
                            {assignedItem ? (
                              <>
                                <WorkbenchPreviewImage
                                  url={resolveItemThumb(assignedItem)}
                                  record={assignedItem}
                                  alt=""
                                  viewRoot={gridScrollRoot}
                                  className="funding-library-modal__stamp-image"
                                />
                                <button
                                  type="button"
                                  className="funding-library-modal__stamp-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearPosition(positionIndex);
                                  }}
                                  aria-label={i18n.t('workbench:materialPicker.clearImage')}
                                >
                                  ×
                                </button>
                              </>
                            ) : null}
                          </div>
                          <span className="funding-library-modal__stamp-name">
                            {stampGroup.name}
                          </span>
                        </div>
                      );
                    });
                    groups.push(
                      <div key={`group-${groupIndex}`} className="funding-library-modal__stamp-group">
                        {groupSquares}
                      </div>,
                    );
                  }
                  return groups;
                })()}
              </div>
            </div>
          )}
        </div>

        <footer className="funding-modal__footer funding-library-modal__footer">
          <span className="funding-library-modal__footer-spacer" aria-hidden="true" />
          <div className="funding-library-modal__footer-actions">
            <button type="button" className="funding-button funding-button--ghost" onClick={onClose}>
              {i18n.t('workbench:materialPicker.cancel')}
            </button>
            <button
              type="button"
              className="funding-button funding-button--accent"
              disabled={selectedCount === 0}
              onClick={handleConfirm}
            >
              {i18n.t('workbench:materialPicker.confirmUse')}
            </button>
          </div>
        </footer>
      </div>
    </div>
    <OfficialGalleryPreviewModal
      open={!!previewItem}
      item={previewItem}
      items={dateFilteredItems}
      activeTab={galleryConfig.previewTab}
      onClose={() => setPreviewItem(null)}
    />
    </>,
    document.body,
  );
}
